import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

/**
 * Saved views = named lenses (filter snapshots) over the master "All Nodes"
 * graph, listed in the sidebar Notion-style. v1 persists to localStorage
 * (per identity); a backend table is the later upgrade.
 *
 * Bridge: GraphView owns the filter state, so it `registerCapture`s a snapshot
 * fn and consumes `pending` to apply a view. The sidebar lists views + calls
 * `applyView` / `createView`.
 */
const SavedViewsContext = createContext(null);

const keyFor = (uid) => `saved_views_${uid || 'anon'}`;

export function SavedViewsProvider({ children }) {
  const { user } = useAuth();
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [pending, setPending] = useState(null); // { filters, nonce } consumed by GraphView
  const captureRef = useRef(null);
  const nonceRef = useRef(0);

  // Load this identity's views.
  useEffect(() => {
    if (!user) { setViews([]); return; }
    try {
      const raw = localStorage.getItem(keyFor(user.id));
      setViews(raw ? JSON.parse(raw) : []);
    } catch { setViews([]); }
    setActiveViewId(null);
  }, [user]);

  const persist = useCallback((next) => {
    setViews(next);
    try { if (user) localStorage.setItem(keyFor(user.id), JSON.stringify(next)); } catch {}
  }, [user]);

  // GraphView registers a fn returning the current filter snapshot.
  const registerCapture = useCallback((fn) => { captureRef.current = fn; }, []);

  // Create a saved view. With no explicitFilters it snapshots the current
  // filters (a plain view); with explicitFilters + settings it can mint a
  // "database" — a tag-defined lens that's empty until nodes get that tag.
  const createView = useCallback((name, explicitFilters, settings) => {
    const filters = explicitFilters || (captureRef.current ? captureRef.current() : {});
    const id = `v_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const view = { id, name: (name || '').trim() || 'Untitled view', filters, ...(settings ? { settings } : {}) };
    persist([...views, view]);
    setActiveViewId(id);
    // Apply immediately so navigating into it shows the (empty) filtered lens.
    nonceRef.current += 1;
    setPending({ filters, nonce: nonceRef.current });
    return view;
  }, [views, persist]);

  const renameView = useCallback((id, name) => {
    const n = (name || '').trim();
    if (!n) return;
    persist(views.map(v => (v.id === id ? { ...v, name: n } : v)));
  }, [views, persist]);

  const deleteView = useCallback((id) => {
    persist(views.filter(v => v.id !== id));
    setActiveViewId(prev => (prev === id ? null : prev));
  }, [views, persist]);

  // Merge UI settings (e.g. wrapText) into a view — per-view display prefs that
  // travel with the view, distinct from its filter snapshot.
  const updateViewSettings = useCallback((id, partial) => {
    persist(views.map(v => (v.id === id ? { ...v, settings: { ...(v.settings || {}), ...partial } } : v)));
  }, [views, persist]);

  // Apply a view (or null = All Nodes / reset). GraphView consumes `pending`.
  const applyView = useCallback((view) => {
    setActiveViewId(view ? view.id : null);
    nonceRef.current += 1;
    setPending({ filters: view ? view.filters : {}, nonce: nonceRef.current });
  }, []);

  // Clear `pending` once GraphView has applied it, so a stale snapshot doesn't
  // re-apply (and re-filter) on the next mount.
  const clearPending = useCallback(() => setPending(null), []);

  return (
    <SavedViewsContext.Provider value={{
      views, activeViewId, pending,
      registerCapture, createView, renameView, deleteView, applyView, updateViewSettings, clearPending,
    }}>
      {children}
    </SavedViewsContext.Provider>
  );
}

export const useSavedViews = () => {
  const ctx = useContext(SavedViewsContext);
  if (!ctx) throw new Error('useSavedViews must be used within a SavedViewsProvider');
  return ctx;
};

export default SavedViewsContext;
